import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { hasPermission, hasAnyPermission, hasAllPermissions } from '../utils/permissions';

/**
 * Custom hook for checking user permissions
 * 
 * Usage:
 * const { can, canAny, canAll } = usePermissions();
 * 
 * if (can('create_member')) {
 *   // Show create member button
 * }
 */
export const usePermissions = () => {
  const { user, loading } = useContext(AuthContext);

  return {
    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission name
     * @returns {boolean}
     */
    can: (permission) => hasPermission(user, permission),

    /**
     * Check if user has any of the given permissions
     * @param {string[]} permissions - Array of permission names
     * @returns {boolean}
     */
    canAny: (permissions) => hasAnyPermission(user, permissions),

    /**
     * Check if user has all of the given permissions
     * @param {string[]} permissions - Array of permission names
     * @returns {boolean}
     */
    canAll: (permissions) => hasAllPermissions(user, permissions),

    /**
     * Check if user is group owner
     * @returns {boolean}
     */
    isOwner: () => user?.is_group_owner || user?.isGroupOwner || false,

    /**
     * Get current user
     * @returns {Object|null}
     */
    user: user,

    /**
     * Loading state
     * @returns {boolean}
     */
    loading: loading,
  };
};
